import { createElement, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { type ColumnProps, type HeadCellProps } from '@/shared/hooks/table/types';
import { type Obj } from '~/common/types';

import useEvent from '@/shared/hooks/useEvent';
import useWindowEvent from '@/shared/hooks/useWindowEvent';

type Props = HeadCellProps & {
  type?: string | null;
  column: ColumnProps;
};

const DefaultHeadCellContent: React.FC<Props> = (props) => {
  const { type = null, column } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);

  const { t } = useTranslation();

  const handleResize = useEvent(() => {
    const container = containerRef.current;
    const parent = container?.parentElement;

    const hasOverflow = !!container && !!parent && container.scrollWidth > parent.clientWidth;

    setIsOverflowing(hasOverflow);
  });

  useLayoutEffect(handleResize, [handleResize]);

  useWindowEvent('resize', handleResize);

  if (column.customHeaderComponent) {
    return createElement(column.customHeaderComponent, props);
  }

  const label = (() => {
    const { title, key } = column;

    if (!type && !title) return '';

    return title ? t(title) : t(`_${type}.columns.${key}`);
  })();

  return (
    <div ref={containerRef} className="inline-block pr-2">
      {!isOverflowing ? (
        <div className="inline-block whitespace-nowrap">{label}</div>
      ) : (
        <Tooltip className="whitespace-nowrap" placement="top" title={label}>
          <div className="inline-block">{label}</div>
        </Tooltip>
      )}
    </div>
  );
};

type WidenedProps = Obj & Props & React.Attributes;

const WidenedDefaultHeadCellContent = (props: WidenedProps) => (
  <DefaultHeadCellContent {...props} />
);

export default {
  Content: DefaultHeadCellContent,
  DefaultHeadCell: WidenedDefaultHeadCellContent,
};
